/**
 * buildVideoSignOff.js
 * Generates a self-contained HTML QA sign-off report for VIDEO ad sessions
 * (DAI/SSAI streams + client-side VAST). Mirrors buildSignOffReport's look so
 * the two reports feel like one product.
 *
 * Input: the array of sessions from buildVideoSessions(), plus a `validate`
 * function (runVideoValidation) so this util stays free of React imports.
 *
 * Sign-off is gated: no hard FAIL checks AND every WARN acknowledged.
 */

import { computeBidNoFill, SRC_LABEL } from '../validation/bidNoFill';

function esc(s = '') {
  return String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
function shortSlot(adUnit = '') {
  return adUnit.split('/').filter(Boolean).slice(-2).join('/') || adUnit;
}
function fmtMs(ms) {
  if (ms == null || ms < 0) return '—';
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}
function statusStyle(s) {
  if (s === 'pass') return { label: 'PASS', color: '#1a6f3c', bg: '#edfaf3', row: '#fff' };
  if (s === 'warn') return { label: 'WARN', color: '#854f0b', bg: '#fffbea', row: '#fffdf4' };
  if (s === 'info') return { label: 'INFO', color: '#1c5f8a', bg: '#eef6fc', row: '#fff' };
  return { label: 'FAIL', color: '#8a1a1a', bg: '#fff0f0', row: '#fff7f7' };
}

const QORD = ['start', 'firstquartile', 'midpoint', 'thirdquartile', 'complete'];
const QLBL = { start: '▶', firstquartile: '25', midpoint: '50', thirdquartile: '75', complete: '✓' };

// Best quartile set across a session's ad breaks.
function sessionQuartiles(session) {
  const breaks = session.adBreaks || (session.tracking ? [{ firedQuartiles: session.tracking.firedQuartiles }] : []);
  let best = new Set();
  for (const b of breaks) {
    const q = b.firedQuartiles || new Set();
    if (q.size > best.size) best = q;
  }
  return best;
}

function sessionTitle(s) {
  return s.sessionType === 'dai_stream' ? (s.displayTitle || s.streamId || 'DAI stream') : (s.adUnit || 'VAST');
}

function quartilePips(q) {
  return QORD.map(k => {
    const on = q.has(k);
    return `<span style="display:inline-block;min-width:20px;text-align:center;padding:1px 0;border-radius:3px;font-size:10px;font-weight:700;margin-right:2px;background:${on ? '#1a6f3c' : '#eee'};color:${on ? '#fff' : '#bbb'}">${QLBL[k]}</span>`;
  }).join('');
}

export function buildVideoSignOff(sessions, { validate, tester = '', deviceLabel = '', pageUrl = '' } = {}) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const items = (sessions || []).map(s => {
    const v = validate ? validate(s) : { checks: [], passed: 0, warned: 0, failed: 0, total: 0 };
    const q = sessionQuartiles(s);
    const completed = q.has('complete');
    const vw = s.viewability;
    const status = v.failed > 0 ? 'fail' : v.warned > 0 ? 'warn' : 'pass';
    const breaks = s.adBreaks || [];
    const filled = breaks.filter(b => b.filled !== false).length;
    const creative = (breaks.find(b => b.creative)?.creative) || s.gamCreative || s.vast?.creative || null;
    const bestTiming = breaks.find(b => b.timing && b.timing.bufferMs != null)?.timing || null;
    return { s, v, q, completed, vw, status, filled, totalBreaks: breaks.length, creative, bestTiming };
  });

  const passes = items.filter(i => i.status === 'pass').length;
  const warns  = items.filter(i => i.status === 'warn').length;
  const fails  = items.filter(i => i.status === 'fail').length;
  const completedCount = items.filter(i => i.completed).length;
  const completionRate = items.length ? Math.round((completedCount / items.length) * 100) : 0;
  const filledBreaks = items.reduce((n, i) => n + i.filled, 0);
  const totalBreaks  = items.reduce((n, i) => n + i.totalBreaks, 0);
  const hlsErrTotal  = items.reduce((n, i) => n + (i.s.hlsErrors?.count || 0), 0);
  const hlsErrSessions = items.filter(i => (i.s.hlsErrors?.count || 0) > 0).length;

  let overall = fails > 0 ? 'FAIL' : warns > 0 ? 'WARN' : 'PASS';
  let overallColor = fails > 0 ? '#8a1a1a' : warns > 0 ? '#854f0b' : '#1a6f3c';

  // Per-session rows.
  const rows = items.map(({ s, v, q, vw, status, filled, totalBreaks: tb, creative, bestTiming }) => {
    const st = statusStyle(status);
    const kind = s.sessionType === 'dai_stream' ? 'DAI/SSAI' : 'VAST';
    const winner = creative?.advertiser || (s.adBreaks || []).find(b => b.advertiser)?.advertiser || '—';
    const dur = creative?.durationSeconds != null ? `${creative.durationSeconds}s` : '—';
    return `
      <tr style="background:${st.row}">
        <td style="font-weight:600;white-space:nowrap">${esc(shortSlot(sessionTitle(s)))}</td>
        <td style="font-size:11px;color:#666">${kind}</td>
        <td style="font-size:11px;color:#666">${esc(s.contentType || '—')}</td>
        <td style="text-align:center;font-size:11px">${tb === 0 ? '—' : `${filled}/${tb}`}</td>
        <td style="white-space:nowrap">${quartilePips(q)}</td>
        <td style="text-align:center;font-size:11px;color:${vw?.present ? '#1a6f3c' : '#bbb'}">${vw?.present ? '👁 ✓' : '—'}</td>
        <td style="font-size:11px;color:#444">${esc(winner)}</td>
        <td style="text-align:center;font-size:11px">${esc(dur)}</td>
        <td style="text-align:center;font-size:11px">${bestTiming?.bufferMs != null ? fmtMs(bestTiming.bufferMs) : '—'}</td>
        <td style="text-align:center">
          <span style="display:inline-block;padding:2px 9px;border-radius:4px;font-weight:700;font-size:11px;background:${st.bg};color:${st.color};border:1px solid ${st.color}40">${st.label}</span>
        </td>
        <td style="font-size:11px;color:#666">✓${v.passed}${v.warned ? ` · ⚠${v.warned}` : ''}${v.failed ? ` · ✗${v.failed}` : ''}</td>
      </tr>`;
  }).join('');

  // Detail blocks per session — full check list.
  const detailBlocks = items.map(({ s, v }, idx) => {
    const checkRows = (v.checks || []).map(c => {
      const st = statusStyle(c.status);
      return `<tr style="background:${st.row}">
        <td style="font-weight:600;white-space:nowrap">${esc(c.label)}</td>
        <td style="text-align:center"><span style="display:inline-block;padding:1px 7px;border-radius:4px;font-weight:700;font-size:10px;background:${st.bg};color:${st.color};border:1px solid ${st.color}40">${st.label}</span></td>
        <td style="font-size:11px;color:#666">${esc(c.detail || '')}</td>
      </tr>`;
    }).join('');
    return `
      <div style="margin:14px 0">
        <div style="font-weight:700;font-size:13px;margin-bottom:6px">${idx + 1}. ${esc(sessionTitle(s))}
          <span style="font-weight:400;color:#888;font-size:11px">— ${s.sessionType === 'dai_stream' ? 'DAI/SSAI' : 'client-side VAST'}</span>
        </div>
        <table><thead><tr><th style="width:30%">Check</th><th style="width:10%;text-align:center">Status</th><th style="text-align:left">Detail</th></tr></thead>
        <tbody>${checkRows || '<tr><td colspan="3" style="color:#999">No checks</td></tr>'}</tbody></table>
      </div>`;
  }).join('');

  // Acknowledgement list — every WARN check needs a human decision.
  let ackIdx = 0;
  const ackItems = items.flatMap(({ s, v }) =>
    (v.checks || []).filter(c => c.status === 'warn' && !c.noTicket).map(c => {
      const i = ackIdx++;
      return `<li style="display:flex;gap:10px;align-items:flex-start;padding:10px 0;border-bottom:1px solid #f0f0f0">
        <input type="checkbox" class="ack-check" id="ack${i}" style="margin-top:3px;width:16px;height:16px;flex-shrink:0">
        <label for="ack${i}" style="flex:1;cursor:pointer">
          <div style="font-weight:600;font-size:13px"><span style="display:inline-block;padding:1px 7px;border-radius:4px;font-size:10px;font-weight:700;background:#fffbea;color:#854f0b;border:1px solid #854f0b40">⚠ WARN</span> ${esc(c.label)}
            <span style="font-weight:400;color:#888;font-size:11px">— ${esc(shortSlot(sessionTitle(s)))}</span></div>
          <div style="font-size:12px;color:#854f0b;margin-top:2px">${esc(c.detail || '')}</div>
        </label></li>`;
    })
  ).join('');
  const ackTotal = ackIdx;

  const hostLabel = (() => {
    try { return new URL(pageUrl || 'http://video').hostname.replace(/^www\./, ''); } catch { return 'Video QA'; }
  })();

  // Video demand wasted — sessions where SpringServe or APS returned a bid and
  // the ad server still came back with an empty VAST. Advisory: it never gates
  // sign-off, but it is the number an ad-ops reviewer is actually looking for.
  const demandWastedSection = (() => {
    try {
      const nf = computeBidNoFill([], sessions || []);
      if (!nf.withDemand) return '';
      if (!nf.count) {
        return `
          <div class="page-divider">💸 Demand wasted — bid returned → no fill</div>
          <p style="font-size:12px;color:#1a6f3c;margin:0 0 6px">None — all ${nf.withDemand} session${nf.withDemand !== 1 ? 's' : ''} with a SpringServe or APS bid returned an ad.</p>`;
      }
      const srcList = Object.entries(nf.bySource)
        .filter(([, n]) => n > 0)
        .map(([s, n]) => `${SRC_LABEL[s]} ${n}`).join(' · ');
      const itemRows = nf.items.map(it => `
        <tr>
          <td style="font-weight:600">${esc(shortSlot(it.adUnit))}</td>
          <td>${esc(it.sources.map(s => SRC_LABEL[s]).join(' + '))}</td>
          <td style="text-align:center;${it.passedToGAM === false ? 'color:#8a1a1a;font-weight:700' : 'color:#666'}">${it.passedToGAM === false ? 'no' : it.passedToGAM === true ? 'yes' : '—'}</td>
          <td style="font-size:11px;color:#666">${esc(it.reason)}</td>
        </tr>`).join('');
      return `
        <div class="page-divider">💸 Demand wasted — ${nf.count} of ${nf.withDemand} session${nf.withDemand !== 1 ? 's' : ''} bid → no fill (${nf.rate}%)</div>
        <p style="font-size:12px;color:#666;margin:0 0 6px">${esc(srcList)}. Video demand bid but the ad server returned an empty VAST — check video line items, creative eligibility and whether the bid tokens were forwarded onto the ad request. Advisory: does not gate sign-off.</p>
        <table>
          <thead><tr><th>Session</th><th style="text-align:left">Demand</th><th style="text-align:center">Bid forwarded</th><th style="text-align:left">No-fill signal</th></tr></thead>
          <tbody>${itemRows}</tbody>
        </table>`;
    } catch { return ''; }
  })();

  const ackSection = ackTotal ? `
    <div class="page-divider">✋ Sign-off acknowledgements — ${ackTotal} warning${ackTotal !== 1 ? 's' : ''} need a human decision</div>
    <p style="font-size:12px;color:#666;margin:0 0 10px">Warnings are not hard failures. Acknowledge each to enable sign-off.</p>
    <ul style="list-style:none">${ackItems}</ul>` : '';

  const signoffBlock = `
    <div class="page-divider">✍️ Sign-off</div>
    <div id="signoff-gate" style="display:none;padding:12px 16px;border-radius:8px;background:#fff8e6;border:1px solid #d8b24a;font-size:13px;color:#7a5800;font-weight:600"></div>
    <div id="signoff-blocked" style="display:none;padding:12px 16px;border-radius:8px;background:#fff0f0;border:1px solid #c87a7a;font-size:13px;color:#8a1a1a;font-weight:600">
      ✗ Cannot sign off — ${fails} session(s) with hard failures must be fixed first.
    </div>
    <div id="signoff-stamp" style="display:none">
      <div style="padding:14px 18px;border-radius:8px;background:#edfaf3;border:1px solid #4caf7d">
        <div style="font-size:15px;font-weight:800;color:#1a6f3c">✓ Video QA approved for sign-off</div>
        <div style="font-size:12px;color:#444;margin-top:6px">${ackTotal ? `All ${ackTotal} warning(s) acknowledged. ` : ''}No outstanding hard failures · completion rate ${completionRate}%.</div>
        <div style="display:flex;gap:40px;margin-top:18px;font-size:12px;color:#333">
          <div>Tester: <b>${esc(tester) || '________________'}</b></div>
          <div>Date: <b>${dateStr}</b></div>
          <div style="flex:1;border-bottom:1px solid #999;align-self:flex-end">Signature:</div>
        </div>
      </div>
    </div>`;

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>Video QA Sign-off — ${esc(hostLabel)} — ${dateStr}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111; background: #fff; padding: 40px; max-width: 1150px; margin: 0 auto; }
  h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
  .meta { font-size: 13px; color: #666; margin-bottom: 28px; } .meta span { margin-right: 20px; }
  .summary-bar { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; padding: 14px 20px; border-radius: 8px; margin-bottom: 28px; border: 2px solid ${overallColor}40; background: #fafafa; }
  .summary-bar .verdict { font-size: 20px; font-weight: 800; color: ${overallColor}; }
  .summary-bar .counts { font-size: 13px; color: #444; } .summary-bar .counts span { margin-right: 14px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead th { background: #f5f5f7; padding: 9px 12px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #666; border-bottom: 2px solid #e0e0e0; }
  tbody tr { border-bottom: 1px solid #f0f0f0; } tbody td { padding: 9px 12px; vertical-align: middle; }
  .page-divider { margin: 28px 0 10px; font-size: 12px; font-weight: 700; color: #444; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e0e0e0; padding-bottom: 6px; }
  footer { margin-top: 32px; font-size: 11px; color: #aaa; border-top: 1px solid #f0f0f0; padding-top: 16px; display: flex; justify-content: space-between; }
  @media print { body { padding: 20px; } .no-print { display: none; } input[type=checkbox] { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body data-fails="${fails}">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px">
    <div>
      <h1>🎬 AdLens FX — Video QA Sign-off</h1>
      <div class="meta">
        <span>📅 ${dateStr} ${timeStr}</span>
        ${tester ? `<span>👤 ${esc(tester)}</span>` : ''}
        ${deviceLabel ? `<span>📱 ${esc(deviceLabel)}</span>` : ''}
      </div>
    </div>
    <button class="no-print" onclick="window.print()" style="padding:8px 18px;background:#007aff;color:white;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer">🖨 Print / Save PDF</button>
  </div>

  <div class="summary-bar">
    <div class="verdict">${overall}</div>
    <div class="counts">
      <span style="color:#1a6f3c;font-weight:700">✓ ${passes} pass</span>
      ${warns ? `<span style="color:#854f0b;font-weight:700">⚠ ${warns} warn</span>` : ''}
      ${fails ? `<span style="color:#8a1a1a;font-weight:700">✗ ${fails} fail</span>` : ''}
      <span style="color:#999">${items.length} session${items.length !== 1 ? 's' : ''}</span>
      <span style="color:#999">·</span>
      <span style="color:#555;font-weight:600">▶ completion ${completionRate}%</span>
      <span style="color:#555;font-weight:600">fill ${filledBreaks}/${totalBreaks}</span>
      ${hlsErrTotal ? `<span style="color:#854f0b;font-weight:700">⚠ ${hlsErrTotal} HLS fail · ${hlsErrSessions} stream${hlsErrSessions !== 1 ? 's' : ''}</span>` : ''}
    </div>
  </div>

  <table>
    <thead><tr>
      <th>Stream / Ad unit</th><th>Type</th><th>Content</th><th style="text-align:center">Fill</th>
      <th>Quartiles</th><th style="text-align:center">View</th><th>Winner</th>
      <th style="text-align:center">Dur</th><th style="text-align:center">Buffer</th><th style="text-align:center">Verdict</th><th>Checks</th>
    </tr></thead>
    <tbody>${rows || '<tr><td colspan="11" style="color:#999;text-align:center;padding:20px">No video sessions captured</td></tr>'}</tbody>
  </table>

  ${demandWastedSection}

  <div class="page-divider">🔎 Per-session validation detail</div>
  ${detailBlocks}
  ${ackSection}
  ${signoffBlock}

  <footer>
    <span>Generated by AdLens FX · Video QA on ${dateStr} at ${timeStr}</span>
    <span>adlensfx.com</span>
  </footer>

  <script>
    (function () {
      var fails = parseInt(document.body.getAttribute('data-fails') || '0', 10);
      function refresh() {
        var boxes = [].slice.call(document.querySelectorAll('.ack-check'));
        var done = boxes.filter(function (b) { return b.checked; }).length;
        var gate = document.getElementById('signoff-gate'), stamp = document.getElementById('signoff-stamp'), blocked = document.getElementById('signoff-blocked');
        gate.style.display = 'none'; stamp.style.display = 'none'; blocked.style.display = 'none';
        if (fails > 0) { blocked.style.display = 'block'; return; }
        if (done < boxes.length) { gate.style.display = 'block'; gate.textContent = '⚠ ' + (boxes.length - done) + ' of ' + boxes.length + ' warning(s) must be acknowledged before sign-off.'; }
        else { stamp.style.display = 'block'; }
      }
      document.addEventListener('change', function (e) { if (e.target && e.target.classList && e.target.classList.contains('ack-check')) refresh(); });
      refresh();
    })();
  </script>
</body></html>`;
}

/** Open the video sign-off report in a new tab (print to PDF from there). */
export function openVideoSignOff(sessions, meta = {}) {
  const html = buildVideoSignOff(sessions, meta);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/** Download the video sign-off report as an HTML file. */
export function downloadVideoSignOff(sessions, meta = {}) {
  const html = buildVideoSignOff(sessions, meta);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `video-qa-signoff-${new Date().toISOString().slice(0, 10)}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
